import { pdfjs } from 'react-pdf';
import { PDFDocument, rgb, StandardFonts, degrees } from 'pdf-lib';

if (!pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
}

const BUBBLE_COLORS = {
  Pass: rgb(0.09, 0.62, 0.29),
  Fail: rgb(0.86, 0.15, 0.15),
  Pending: rgb(0.15, 0.39, 0.92),
};

function bubbleColor(status) {
  return BUBBLE_COLORS[status] || BUBBLE_COLORS.Pending;
}

function percentToPdfPoint(viewport, px, py) {
  const vx = (px / 100) * viewport.width;
  const vy = (py / 100) * viewport.height;
  const [pdfX, pdfY] = viewport.convertToPdfPoint(vx, vy);
  return { x: pdfX, y: pdfY };
}

function drawCenteredUprightText(page, font, center, numStr, fontSize, pageRotation) {
  const textWidth = font.widthOfTextAtSize(numStr, fontSize);
  const rad = (pageRotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = textWidth / 2;
  const dy = fontSize / 2.8;
  const anchorX = center.x - (cos * dx - sin * dy);
  const anchorY = center.y - (sin * dx + cos * dy);

  page.drawText(numStr, {
    x: anchorX,
    y: anchorY,
    size: fontSize,
    font,
    color: rgb(1, 1, 1),
    rotate: degrees(pageRotation),
  });
}

function drawBubble(page, font, viewport, pageRotation, bubble) {
  const color = bubbleColor(bubble.status);
  const { x, y } = percentToPdfPoint(viewport, bubble.x, bubble.y);
  const radius = Math.max(7, viewport.width * 0.013);

  if (bubble.ax !== undefined && bubble.ay !== undefined) {
    const anchor = percentToPdfPoint(viewport, bubble.ax, bubble.ay);
    page.drawLine({
      start: { x, y },
      end: { x: anchor.x, y: anchor.y },
      thickness: 0.8,
      color,
      opacity: 0.75,
    });
    page.drawCircle({
      x: anchor.x,
      y: anchor.y,
      size: radius * 0.35,
      color,
      borderColor: rgb(1, 1, 1),
      borderWidth: 1,
    });
  }

  page.drawCircle({ x, y, size: radius + 2, color: rgb(1, 1, 1) });
  page.drawCircle({
    x,
    y,
    size: radius + 1,
    color: rgb(1, 1, 1),
    borderColor: color,
    borderWidth: 1.5,
  });
  page.drawCircle({ x, y, size: radius, color });

  const numStr = String(bubble.number);
  const fontSize = numStr.length > 1 ? radius * 0.78 : radius * 0.9;
  drawCenteredUprightText(page, font, { x, y }, numStr, fontSize, pageRotation);
}

export async function generateBubbledPdf(pdfBytes, bubbles) {
  const pdfJsDoc = await pdfjs.getDocument({ data: pdfBytes.slice(0) }).promise;
  const pdfJsPage = await pdfJsDoc.getPage(1);
  const viewport = pdfJsPage.getViewport({ scale: 1 });
  const pageRotation = pdfJsPage.rotate || 0;

  const pdfDoc = await PDFDocument.load(pdfBytes);
  const page = pdfDoc.getPages()[0];
  const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  for (const bubble of bubbles) {
    drawBubble(page, font, viewport, pageRotation, bubble);
  }

  return pdfDoc.save();
}
